// src/pages/RegisterPage.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./RegisterPage.css";
import { acceptTermsForUserData } from "../services/termsDataService";

import {
  registerWithEmail,
  authenticateWithGoogle,
  authenticateWithApple,
} from "../services/authDataService";
import { getFooterTexts } from "../services/footerDataService";

import GateIcon from "../components/GateIcon";
import OfflineBanner from "../components/OfflineBanner";
import SpeechIcon from "../components/SpeechIcon";

function RegisterPage() {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsRequiredModal, setShowTermsRequiredModal] = useState(false);
  const [termsRequiredText, setTermsRequiredText] = useState("");

  const [showAGB, setShowAGB] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const navigate = useNavigate();

  const [footerTexts, setFooterTexts] = useState({});
  const [footerLoading, setFooterLoading] = useState(false);
  const [footerLoadError, setFooterLoadError] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowAGB(false);
        setShowDatenschutz(false);
        setShowTermsRequiredModal(false);
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });

        setIsOffline(false);
      } catch (err) {
        console.warn("Keine Server-Verbindung möglich (RegisterPage):", err);
        setIsOffline(true);
      }
    };

    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  useEffect(() => {
    let mounted = true;

    const fetchFooterTexts = async () => {
      setFooterLoading(true);
      setFooterLoadError("");

      try {
        const cached = localStorage.getItem("footerTexts_cache_v1");

        if (cached && mounted) {
          setFooterTexts(JSON.parse(cached));
        }
      } catch (e) {
        console.warn("Footer Cache konnte nicht gelesen werden:", e);
      }

      try {
        const data = await getFooterTexts();

        if (!mounted) return;

        setFooterTexts(data || {});

        try {
          localStorage.setItem("footerTexts_cache_v1", JSON.stringify(data || {}));
        } catch (e) {
          console.warn("Footer Cache konnte nicht gespeichert werden:", e);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Footer-Texte:", error);

        if (mounted) {
          setFooterLoadError("Texte konnten nicht geladen werden.");
        }
      } finally {
        if (mounted) setFooterLoading(false);
      }
    };

    fetchFooterTexts();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if ((showAGB || showDatenschutz) && modalRef.current) {
      modalRef.current.focus();
    }
  }, [showAGB, showDatenschutz]);

  const openTermsRequiredModal = (providerLabel = "") => {
    setTermsRequiredText(
      providerLabel
        ? `Bitte akzeptiere zuerst die Nutzungsbedingungen und Datenschutzbestimmungen, bevor du dich mit ${providerLabel} registrierst.`
        : "Bitte akzeptiere zuerst die Nutzungsbedingungen und Datenschutzbestimmungen, um dich zu registrieren."
    );

    setShowTermsRequiredModal(true);
  };

  const validateTermsAccepted = (providerLabel = "") => {
    if (acceptedTerms) return true;

    openTermsRequiredModal(providerLabel);
    return false;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!validateTermsAccepted()) {
      return;
    }

    const passwordRequirements =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

    if (!passwordRequirements.test(password)) {
      setErrorMessage(
        "❗ Dein Passwort erfüllt nicht die Anforderungen:\nMind. 8 Zeichen, 1 Großbuchstabe, 1 Zahl und 1 Sonderzeichen."
      );
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("❗ Die Passwörter stimmen nicht überein.");
      return;
    }

    setIsLoading(true);

    const timeoutMs = 12000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("register-timeout")), timeoutMs)
    );

    try {
      const createdUser = await Promise.race([
        registerWithEmail({ email, password, username }),
        timeoutPromise,
      ]);

      if (!createdUser?.uid) {
        throw new Error("no-uid-returned");
      }

      await acceptTermsForUserData(
        createdUser.uid,
        createdUser.email || email.trim()
      );

      navigate("/confirmation");
    } catch (error) {
      console.error("❌ Registrierung fehlgeschlagen:", error);

      if (error?.message === "register-timeout") {
        setErrorMessage(
          "Die Registrierung dauert gerade zu lange. Bitte prüfe deine Verbindung und versuche es erneut."
        );
      } else if (error.code === "auth/email-already-in-use") {
        setErrorMessage("Diese E-Mail ist bereits registriert.");
      } else if (error.code === "auth/invalid-email") {
        setErrorMessage("Die angegebene E-Mail-Adresse ist ungültig.");
      } else if (error.code === "auth/weak-password") {
        setErrorMessage(
          "Das Passwort ist zu schwach. Bitte beachte die Passwortanforderungen."
        );
      } else if (error.code === "auth/network-request-failed") {
        setErrorMessage(
          "Es gab ein Netzwerkproblem. Bitte überprüfe deine Internetverbindung und versuche es erneut."
        );
      } else if (error.message === "no-uid-returned") {
        setErrorMessage(
          "Es ist ein technisches Problem bei der Registrierung aufgetreten. Bitte versuche es später erneut."
        );
      } else {
        setErrorMessage(
          "Registrierung fehlgeschlagen. Bitte versuche es erneut oder kontaktiere den Support."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setErrorMessage("");

    if (!validateTermsAccepted("Google")) {
      return;
    }

    setIsLoading(true);

    try {
      const user = await authenticateWithGoogle({
        fallbackUsername: username,
        navigate,
        navigateTo: null,
      });

      if (!user?.uid) {
        throw new Error("no-user-returned");
      }

      await acceptTermsForUserData(user.uid, user.email || email.trim());

      navigate("/browse", { replace: true });
    } catch (e) {
      console.error("Google Sign-In fehlgeschlagen (RegisterPage):", e);

      if (e?.code === "auth/popup-closed-by-user") {
        setErrorMessage("Google Login abgebrochen.");
      } else if (e?.code === "auth/cancelled-popup-request") {
        setErrorMessage("Google Login wurde abgebrochen. Bitte erneut versuchen.");
      } else if (e?.code === "auth/unauthorized-domain") {
        setErrorMessage(
          "Domain nicht erlaubt. Prüfe Firebase Auth → Authorized domains."
        );
      } else if (e?.code === "auth/network-request-failed") {
        setErrorMessage("Netzwerkproblem. Bitte Verbindung prüfen und erneut versuchen.");
      } else {
        setErrorMessage(
          "Google Registrierung/Login fehlgeschlagen. Bitte versuche es erneut."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleRegister = async () => {
    setErrorMessage("");

    if (!validateTermsAccepted("Apple")) {
      return;
    }

    setIsLoading(true);

    try {
      const user = await authenticateWithApple({
        fallbackUsername: username,
        navigate,
        navigateTo: null,
      });

      if (!user?.uid) {
        throw new Error("no-user-returned");
      }

      await acceptTermsForUserData(user.uid, user.email || email.trim());

      console.log("🍎 RegisterPage Apple success", {
        uid: user.uid,
        email: user.email,
      });

      navigate("/browse", { replace: true });
    } catch (error) {
      console.error("Apple Registrierung/Login fehlgeschlagen:", error);

      const code = String(error?.code || "");
      const msg = String(error?.message || "");

      if (
        code === "auth/popup-closed-by-user" ||
        msg.toLowerCase().includes("canceled") ||
        msg.toLowerCase().includes("cancelled")
      ) {
        setErrorMessage("Apple Login abgebrochen.");
      } else {
        setErrorMessage(
          "Apple Registrierung/Login fehlgeschlagen. Bitte versuche es erneut."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderPlainText = (text) => {
    const normalized = String(text || "").replace(/\r\n/g, "\n").trim();
    const paragraphs = normalized.split(/\n\s*\n/).filter(Boolean);

    return (
      <div className="legal-text">
        {paragraphs.map((para, idx) => {
          const lines = para.split("\n");

          return (
            <p key={idx}>
              {lines.map((line, i) => {
                const trimmed = line.trim();
                const isParagraphTitle = /^§\s*\d+/i.test(trimmed);

                return (
                  <React.Fragment key={i}>
                    {isParagraphTitle ? (
                      <strong className="legal-paragraph-title">{trimmed}</strong>
                    ) : (
                      trimmed
                    )}
                    {i < lines.length - 1 && <br />}
                  </React.Fragment>
                );
              })}
            </p>
          );
        })}
      </div>
    );
  };

  const renderModal = (type) => {
    const close = () =>
      type === "agb" ? setShowAGB(false) : setShowDatenschutz(false);

    let content = "";
    if (type === "agb") content = footerTexts.agb || "";
    if (type === "datenschutz") content = footerTexts.datenschutz || "";

    return (
      <div className="footer-modal-overlay" onClick={close}>
        <div
          className="footer-modal-content"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          ref={modalRef}
        >
          {footerLoadError ? (
            <p>{footerLoadError}</p>
          ) : footerLoading ? (
            <p>Inhalt wird geladen...</p>
          ) : content ? (
            renderPlainText(content)
          ) : (
            <p>Inhalt wird geladen...</p>
          )}

          <button
            onClick={close}
            className="green-button"
            style={{ marginTop: 16 }}
          >
            Schließen
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="RegisterPage">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      <header className="header">
        <img
          src="/logo-edens-ernte-transparent.png"
          alt="Logo"
          className="browse-logo"
          onClick={() => navigate("/")}
        />

        <SpeechIcon className="speech-icon no-tap-highlight" />
        <GateIcon className="login-icon" />

        <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>
      </header>

      <div className="register-container">
        <h2>Registriere dich jetzt!</h2>

        <p>
          Erstelle ein Konto, um Angebote zu durchsuchen oder selbst Inserate
          einzustellen.
        </p>

        <form className="register-form" onSubmit={handleRegister}>
          <input
            type="text"
            placeholder="Benutzername"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={isLoading}
          />

          <input
            type="email"
            placeholder="E-Mail-Adresse"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />

          <div className="password-container">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />

            <span
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "👁️🚫" : "👁️"}
            </span>
          </div>

          <div className="password-container">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Passwort bestätigen"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
            />

            <span
              className="toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? "👁️🚫" : "👁️"}
            </span>
          </div>

          <div className="password-rules">
            <p>Dein Passwort muss enthalten:</p>
            <ul>
              <li>Mindestens 8 Zeichen</li>
              <li>Mindestens 1 Zahl</li>
              <li>Mindestens 1 Großbuchstabe</li>
              <li>Mindestens 1 Sonderzeichen (! ? % $)</li>
            </ul>
          </div>

          <div className="checkbox-container">
            <input
              type="checkbox"
              id="agb-checkbox"
              required
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={isLoading}
            />

            <label htmlFor="agb-checkbox">
              Ich akzeptiere die{" "}
              <button
                type="button"
                className="agb-link-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAGB(true);
                }}
              >
                Nutzungsbedingungen
              </button>
              , einschließlich der Regeln zu unzulässigen Inhalten und
              missbräuchlichem Verhalten, sowie die{" "}
              <button
                type="button"
                className="agb-link-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDatenschutz(true);
                }}
              >
                Datenschutzbestimmungen
              </button>
              .
            </label>
          </div>

          <button type="submit" className="green-button" disabled={isLoading}>
            {isLoading ? "Bitte warten…" : "Registrieren"}
          </button>

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="or-divider">
            <span>ODER</span>
          </div>

          <div className="one-click-options">
            <button
              className="green-button"
              type="button"
              onClick={handleGoogleRegister}
              disabled={isLoading}
            >
              Mit Google registrieren
            </button>

            <button
              className="green-button"
              type="button"
              onClick={handleAppleRegister}
              disabled={isLoading}
            >
              Mit Apple registrieren
            </button>
          </div>
        </form>

        <div className="login-link">
          <p>
            Bereits ein Konto?{" "}
            <span className="link-text" onClick={() => navigate("/login")}>
              Jetzt einloggen
            </span>
          </p>
        </div>
      </div>

      {showAGB && renderModal("agb")}
      {showDatenschutz && renderModal("datenschutz")}

      {showTermsRequiredModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowTermsRequiredModal(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Zustimmung erforderlich</h3>

            <p>{termsRequiredText}</p>

            <div className="modal-buttons">
              <button
                type="button"
                className="green-button"
                onClick={() => setShowTermsRequiredModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegisterPage;